import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { PlusOutlined, Dropdown } from "@/config/antd";
import {
  createPollSurveyAsync,
  fetchPollByAny,
  updatePollSurvey,
} from "@/store/redux/slices/polls_&_survey/polls_&_survey";
import { uploadFile } from "@/store/redux/slices/user/file_upload_slice";
import { useLoader } from "@/hooks/LoaderProvider";
import Back from "@/components/specific/Back";
import Input from "@/components/common/inputs/Input";
import TextEditor from "@/components/common/inputs/Text_Editor";
import FileInput from "@/components/common/inputs/File_Input";
import Button from "@/components/common/Button";
import CustomScheduleInput from "@/components/specific/Custom_Schedule_Input";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import Success from "@/components/common/alerts/Success";
import Error from "@/components/common/alerts/Error";
import Breadcrumb from "@/components/specific/Breadcrumb";
import { getOrganizationId } from "@/utils/token_functions";
import Steps from "@/components/common/Steps";

/** Build the public dynamic polls URL for respondents (HashRouter). */
const getDynamicPollShareUrl = (pollId) => {
  if (!pollId) return "";
  return `${window.location.origin}/#/dynamic/polls/${pollId}`;
};

/** Resolve created poll _id from various API response shapes. */
const extractCreatedPollId = (response) => {
  if (!response) return null;
  if (response._id) return response._id;
  if (response.data?._id) return response.data._id;
  if (Array.isArray(response.data) && response.data[0]?._id) {
    return response.data[0]._id;
  }
  return null;
};

/** Resolve poll document from by-any API item (flat or nested under `poll`). */
const normalizePollFromFetchItem = (item) => {
  if (!item) return null;
  return item.poll ?? item;
};

const AddNewPollsAndSurvey = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams(); // Get ID if in edit mode
  const isEditMode = Boolean(id);
  const { showLoader, hideLoader } = useLoader();

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdPollId, setCreatedPollId] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showOptionSuccessModal, setShowOptionSuccessModal] = useState(false);
  const [optionSuccessAction, setOptionSuccessAction] = useState("saved");

  // Saved poll options (array sent to API); draft option is edited in formData.option
  const [savedOptions, setSavedOptions] = useState([]);
  const [editingOptionIndex, setEditingOptionIndex] = useState(null);

  const createEmptyOption = () => ({ name: "" });

  const [formData, setFormData] = useState({
    ...(isEditMode && { _id: "" }), // Only include _id if in edit mode
    title: "",
    description: "",
    media: "",
    duration: "24",
    customDate: "",
    customTimeFrom: "",
    customTimeTo: "",
    option: createEmptyOption(),
  });

  // Define sections for tracking in the Steps component
  const formSections = [
    { title: "Survey Information" },
    { title: "Media" },
    { title: "Duration" },
    { title: "Options" },
  ];

  // State to track the current active section
  const [currentSection, setCurrentSection] = useState(0);

  // Refs for each section to detect visibility
  const sectionRefs = useRef([]);

  // Track which sections have been completed
  const [sectionsCompleted, setSectionsCompleted] = useState([]);

  // Check if a section is complete
  const checkSectionCompletion = useCallback(() => {
    const completed = [];

    // Section 0: Survey Information
    if (formData.title && formData.description) {
      completed.push(0);
    }

    // Section 1: Media
    if (formData.media) {
      completed.push(1);
    }

    // Section 2: Duration
    if (
      formData.duration &&
      (formData.duration !== "custom" ||
        (formData.customDate &&
          formData.customTimeFrom &&
          formData.customTimeTo))
    ) {
      completed.push(2);
    }

    if (savedOptions.length > 0 || formData.option.name.trim()) {
      completed.push(3);
    }

    setSectionsCompleted(completed);
  }, [formData, savedOptions]);

  // Check section completion whenever formData changes
  useEffect(() => {
    checkSectionCompletion();
  }, [formData, checkSectionCompletion]);

  // Function to check which section is currently most visible
  useEffect(() => {
    const handleScroll = () => {
      const viewportHeight = window.innerHeight;
      const viewportCenter = window.scrollY + viewportHeight / 2;

      // Find the section closest to the center of the viewport
      let closestSection = 0;
      let closestDistance = Infinity;

      sectionRefs.current.forEach((sectionElement, index) => {
        if (sectionElement) {
          const rect = sectionElement.getBoundingClientRect();
          const sectionCenter = window.scrollY + rect.top + rect.height / 2;
          const distance = Math.abs(viewportCenter - sectionCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestSection = index;
          }
        }
      });

      setCurrentSection(closestSection);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const convertTo24Hour = (time12h) => {
    if (!time12h) return "";

    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":");

    if (hours === "12") {
      hours = "00";
    }

    if (modifier === "PM") {
      hours = parseInt(hours, 10) + 12;
    }

    return `${hours}:${minutes}`;
  };

  // Add useEffect to fetch poll data when in edit mode
  useEffect(() => {
    const fetchPollData = async () => {
      if (isEditMode) {
        try {
          showLoader();
          const result = await dispatch(
            fetchPollByAny({ key: "_id", value: id })
          ).unwrap();

          const pollData = normalizePollFromFetchItem(result.data?.[0]);

          if (!pollData) {
            setErrorMessage("Poll not found");
            setShowErrorModal(true);
            return;
          }

          const schedule = pollData.schedule ?? {};

          // Convert API time format (12-hour) to 24-hour format for time picker
          const startTime24 = convertTo24Hour(schedule.startTime);
          const endTime24 = convertTo24Hour(schedule.endTime);

          // API may return options as an array or a single object
          const apiOptions = pollData.options;
          const rawOptions = Array.isArray(apiOptions)
            ? apiOptions
            : apiOptions?.name
              ? [apiOptions]
              : [];
          const normalizedOptions = rawOptions.map((opt) => ({
            name: opt.name || "",
          }));

          const startDate = schedule.startDate ?? "";
          const endDate = schedule.endDate ?? "";

          setSavedOptions(normalizedOptions);
          setFormData({
            _id: pollData._id,
            title: pollData.name ?? "",
            description: pollData.description ?? "",
            media: pollData.media || "",
            duration: startDate && endDate ? "custom" : "24",
            customDate:
              startDate && endDate ? `${startDate} - ${endDate}` : "",
            customTimeFrom: startTime24,
            customTimeTo: endTime24,
            option: createEmptyOption(),
          });
        } catch (error) {
          console.error("Failed to fetch poll data:", error);
          setErrorMessage("Failed to fetch poll data");
          setShowErrorModal(true);
        } finally {
          hideLoader();
        }
      }
    };

    fetchPollData();
  }, [dispatch, hideLoader, id, isEditMode, showLoader]);

  const handleSuccessProceed = () => {
    showLoader();
    setShowSuccessModal(false);
    setCreatedPollId(null);
    setTimeout(() => {
      hideLoader();
      if (isEditMode) {
        navigate(`/dashboard/polls/${id}`);
      } else {
        navigate("/dashboard/polls");
      }
    }, 2000);
  };

  const handleBack = () => {
    navigate("/dashboard/polls");
  };

  const handleEditorChange = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleCustomDateChange = (dateRange) => {
    setFormData((prev) => ({ ...prev, customDate: dateRange }));
  };

  const handleCustomTimeFromChange = (time) => {
    // Convert 24-hour time to 12-hour format with AM/PM
    const date = new Date(`2000-01-01T${time}`);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    setFormData((prev) => ({
      ...prev,
      customTimeFrom: formattedTime,
    }));
  };

  const handleCustomTimeToChange = (time) => {
    // Convert 24-hour time to 12-hour format with AM/PM
    const date = new Date(`2000-01-01T${time}`);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    setFormData((prev) => ({
      ...prev,
      customTimeTo: formattedTime,
    }));
  };

  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleFileChange = (file) => {
    if (file) {
      setFormData((prev) => ({
        ...prev,
        media: file,
      }));
    }
  };

  const handleOptionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      option: { name: value },
    }));
  };

  const isOptionFormValid = formData.option.name.trim() !== "";

  const handleSaveOption = () => {
    if (!isOptionFormValid) return;

    showLoader();

    const optionPayload = { name: formData.option.name.trim() };

    if (editingOptionIndex !== null) {
      const updatedOptions = savedOptions.map((opt, index) =>
        index === editingOptionIndex ? optionPayload : opt
      );

      setTimeout(() => {
        setSavedOptions(updatedOptions);
        setFormData((prev) => ({ ...prev, option: createEmptyOption() }));
        setEditingOptionIndex(null);
        setOptionSuccessAction("updated");
        setShowOptionSuccessModal(true);
        hideLoader();
      }, 500);
    } else {
      setTimeout(() => {
        setSavedOptions((prev) => [...prev, optionPayload]);
        setFormData((prev) => ({ ...prev, option: createEmptyOption() }));
        setOptionSuccessAction("saved");
        setShowOptionSuccessModal(true);
        hideLoader();
      }, 500);
    }
  };

  const handleModifyOption = (option, index) => {
    setFormData((prev) => ({
      ...prev,
      option: { name: option.name },
    }));
    setEditingOptionIndex(index);
  };

  const handleRemoveOption = (index) => {
    showLoader();

    setTimeout(() => {
      setSavedOptions((prev) => prev.filter((_, i) => i !== index));
      if (editingOptionIndex === index) {
        setEditingOptionIndex(null);
        setFormData((prev) => ({ ...prev, option: createEmptyOption() }));
      } else if (editingOptionIndex !== null && editingOptionIndex > index) {
        setEditingOptionIndex((prev) => prev - 1);
      }
      hideLoader();
    }, 500);
  };

  const saveOptionButtonText = editingOptionIndex !== null
    ? "Update option"
    : "Save and add new option";

  const canSubmitPoll = savedOptions.length > 0;

  const handleSubmit = async () => {
    if (!canSubmitPoll) {
      setErrorMessage("Add at least one survey option before proceeding.");
      setShowErrorModal(true);
      return;
    }
    showLoader();
    try {
      let startDate, endDate, startTime, endTime;

      if (formData.duration === "custom") {
        // Handle custom date/time
        const [startDateStr, endDateStr] = formData.customDate.split(" - ");

        // Convert date strings to proper format (YYYY-MM-DD)
        const formatDate = (dateStr) => {
          const date = new Date(dateStr);
          return date.toISOString().split("T")[0];
        };

        startDate = formatDate(startDateStr);
        endDate = formatDate(endDateStr);
        startTime = formData.customTimeFrom;
        endTime = formData.customTimeTo;
      } else {
        // For fixed durations (24, 48)
        const now = new Date();
        startDate = now.toISOString().split("T")[0];

        // Parse hours from duration value
        const hours = parseInt(formData.duration);
        const endDateTime = new Date(now.getTime() + hours * 60 * 60 * 1000);
        endDate = endDateTime.toISOString().split("T")[0];

        // Set default times for fixed durations
        startTime = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        endTime = endDateTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }

      let mediaUrl = formData.media;
      if (formData.media instanceof File) {
        try {
          const uploadResponse = await dispatch(
            uploadFile({ file: formData.media, folder: "polls" })
          ).unwrap();
          mediaUrl = uploadResponse.fileUrl;
        } catch (uploadError) {
          console.error("File upload failed:", uploadError);
          hideLoader();
          setErrorMessage("Failed to upload media file. Please try again.");
          setShowErrorModal(true);
          return;
        }
      }

      // Sanitize the description
      const sanitizedDescription = DOMPurify.sanitize(formData.description, {
        ALLOWED_TAGS: [],
      });

      const pollData = {
        _id: formData._id,
        name: formData.title.trim(),
        description: sanitizedDescription,
        media: mediaUrl,
        schedule: {
          startDate,
          endDate,
          startTime,
          endTime,
        },
        options: savedOptions.map((opt) => ({ name: opt.name })),
        organizationID: getOrganizationId(),
      };

      if (isEditMode) {
        // For update, wrap the data in an array
        await dispatch(updatePollSurvey([pollData])).unwrap();
        setCreatedPollId(null);
      } else {
        const createResult = await dispatch(
          createPollSurveyAsync(pollData)
        ).unwrap();
        setCreatedPollId(extractCreatedPollId(createResult));
      }

      setShowSuccessModal(true);
    } catch (error) {
      console.error("Failed to create/update poll:", error);
      setErrorMessage(error.message || "Failed to create/update poll");
      setShowErrorModal(true);
    } finally {
      hideLoader();
    }
  };

  const durationOptions = [
    { value: "24", label: "24 Hours" },
    { value: "48", label: "48 Hours" },
    { value: "custom", label: "Custom" },
  ];

  const handleDurationChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      duration: value,
    }));
  };

  // breadcrumb items
  const items = [
    { title: "Polls and Surveys", link: "/dashboard/polls" },
    { title: "Add New", link: "" },
  ];

  return (
    <div className="w-full grid-cols-4 gap-10 py-6 text-left lg:grid sm:py-0">
      <div className="col-span-3  lg:border-r">
        <Breadcrumb items={items} />
        {/* Back Button */}
        <Back text="Back" onClick={handleBack} />

        {/* Main Content */}
        <div className="lg:p-5 my-5 rounded-lg lg:border">
          <h1 className="mb-2 text-2xl font-semibold">
            {isEditMode ? "Edit your Survey" : "Setup your Survey"}
          </h1>
          <p className="mb-8 text-gray-600">
            Fill the form below. All fields are required to be filled with
            accurate information.
          </p>

          {/* Survey Details Section */}
          <div
            className="space-y-6"
            ref={(element) => {
              sectionRefs.current[0] = element;
            }}>
            <h2 className="text-lg font-medium">
              Describe what this survey is all about
            </h2>

            {/* Title Input */}
            <Input
              label="SURVEY TITLE"
              placeholder="Edit"
              value={formData.title}
              onChange={handleInputChange("title")}
            />

            {/* Description Editor */}
            <TextEditor
              label="SURVEY DESCRIPTION"
              placeholder="Enter Texts"
              value={formData.description}
              onChange={handleEditorChange}
            />
          </div>

          {/* Media Section */}
          <div
            className="mt-8 space-y-4"
            ref={(element) => {
              sectionRefs.current[1] = element;
            }}>
            <h2 className="text-lg font-medium">Media</h2>
            <p className="text-gray-600">
              Add up images to make easily make your campaign easy to see
            </p>

            <FileInput
              id="survey-media"
              placeholder={
                formData.media instanceof File
                  ? formData.media.name
                  : formData.media
                  ? "Media uploaded"
                  : "File Name.png"
              }
              onChange={handleFileChange}
              accept="image/*"
            />
          </div>

          {/* Duration Section */}
          <div
            className="mt-8 space-y-4"
            ref={(element) => {
              sectionRefs.current[2] = element;
            }}>
            <h2 className="text-lg font-medium">Event Duration and Schedule</h2>
            <p className="text-gray-600">
              Select the total time this event will take
            </p>

            <CustomScheduleInput
              options={durationOptions}
              value={formData.duration}
              onChange={handleDurationChange}
              onCustomDateChange={handleCustomDateChange}
              onCustomTimeFromChange={handleCustomTimeFromChange}
              onCustomTimeToChange={handleCustomTimeToChange}
              customDate={formData.customDate}
              customTimeFrom={formData.customTimeFrom}
              customTimeTo={formData.customTimeTo}
            />
          </div>

          {/* Options Section */}
          <div
            className="mt-8 space-y-4"
            ref={(element) => {
              sectionRefs.current[3] = element;
            }}>
            <div className="space-y-4">
              <h2 className="text-lg font-medium">Options</h2>
              <p className="text-gray-600">
                Add information for your survey options
              </p>
              <p className="text-small text-darkGrey italic font-bold">
                NOTE: You can add as many options as possible by clicking on
                &quot;Save and add new option&quot;
              </p>

              {/* Saved options list */}
              {savedOptions.length > 0 && (
                <div className="mb-6">
                  {savedOptions.map((option, index) => (
                    <div
                      key={`${option.name}-${index}`}
                      className="flex items-center justify-between p-4 border-b border-gray-200">
                      <div className="flex flex-col">
                        <span className="font-none text-primary text-small-xs">
                          OPTION
                        </span>
                        <span className="font-bold text-regular">
                          {option.name}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Button
                          text="REMOVE"
                          className="mr-2 border border-primary text-small w-[85px] h-[34px]"
                          customBgColor="white"
                          customTextColor="primary"
                          onClick={() => handleRemoveOption(index)}
                          fontWeight="none"
                        />
                        <Dropdown
                          menu={{
                            items: [
                              {
                                key: "1",
                                label: "Modify option",
                                onClick: () => handleModifyOption(option, index),
                              },
                            ],
                          }}
                          trigger={["click"]}>
                          <div onClick={(e) => e.preventDefault()}>
                            <CustomMoreOptions
                              variant="filled"
                              orientation="vertical"
                              color="black"
                            />
                          </div>
                        </Dropdown>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Input
                label="OPTION TITLE"
                placeholder="Enter option title"
                value={formData.option.name}
                onChange={(e) => handleOptionChange(e.target.value)}
              />

              <Button
                text={saveOptionButtonText}
                icon={<PlusOutlined />}
                onClick={handleSaveOption}
                disabled={!isOptionFormValid}
                className="w-full border border-primary cursor-pointer text-bold h-[36px]"
                customBgColor="white"
                customTextColor="primary"
                iconPosition="left"
                fontWeight="none"
              />
            </div>
          </div>

          {/* Proceed Button */}
          <div className="mt-8">
            <Button
              text={isEditMode ? "Update" : "Proceed"}
              color="primary"
              isActive={canSubmitPoll}
              className={`w-full ${
                !canSubmitPoll && "bg-darkBlue text-primary cursor-not-allowed"
              }`}
              customBgColor={canSubmitPoll ? "primary" : "darkBlue"}
              customTextColor={canSubmitPoll ? "white" : "primary"}
              onClick={handleSubmit}
              disabled={!canSubmitPoll}
            />
          </div>
        </div>
      </div>

      {/* Steps component for single-page form */}
      <div className="hidden mt-10 lg:block h-[90vh] sticky top-[80px]">
        <Steps
          current={currentSection}
          steps={formSections}
          mode="single-page"
          sectionsCompleted={sectionsCompleted}
        />
      </div>

      <Success
        isVisible={showSuccessModal}
        onClose={handleSuccessProceed}
        header={isEditMode ? "Poll Updated" : "Poll Created"}
        paragraph={`Your poll/survey has been successfully ${
          isEditMode ? "updated" : "created"
        }.`}
        copyLinkUrl={
          !isEditMode && createdPollId
            ? getDynamicPollShareUrl(createdPollId)
            : undefined
        }
        buttonText="Close"
      />

      <Error
        isVisible={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        header={isEditMode ? "Update Failed" : "Creation Failed"}
        paragraph={errorMessage}
        buttonText="Try Again"
        recoveryPath="/dashboard/polls"
      />

      <Success
        isVisible={showOptionSuccessModal}
        onClose={() => setShowOptionSuccessModal(false)}
        header={
          optionSuccessAction === "updated" ? "Option Updated" : "Option Saved"
        }
        paragraph={
          optionSuccessAction === "updated"
            ? "Your option has been updated successfully."
            : "Your option has been saved successfully. Add another or proceed when ready."
        }
        buttonText="Close"
        onButtonClick={() => setShowOptionSuccessModal(false)}
      />
    </div>
  );
};

export default AddNewPollsAndSurvey;
